/**
 * Where the money came from, and where it went.
 *
 * Grouped by what the other side of the entry was, so one instalment shows as
 * part loan repayment and part interest without anybody splitting it by hand.
 * The headings that are movements rather than trading — a loan received, money
 * moved between our own accounts — are marked, because a report that let a
 * ₹50,00,000 loan read as a good month would be worse than no report.
 *
 * Every figure opens: click a heading and the entries behind it come from the
 * same classification that produced the total, never a second query that
 * nearly agrees.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, Empty, ErrorNote, Loading, Note } from '../components/ui'

interface Group {
  group: string
  amount_paise: number
  count: number
  is_trading: boolean
}

interface Month {
  month: string
  in_paise: number
  out_paise: number
  net_paise: number
}

interface Flows {
  from_date: string
  to_date: string
  money_in: Group[]
  money_out: Group[]
  in_total_paise: number
  out_total_paise: number
  net_paise: number
  trading_in_paise: number
  trading_out_paise: number
  by_month?: Month[]
}

interface FlowLine {
  entry_id: string
  journal_number: string
  journal_date: string
  amount_paise: number
  incoming: boolean
  account_name: string
  memo: string | null
  source_type: string | null
}

function financialYearStart(): string {
  const now = new Date()
  const year = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
  return `${year}-04-01`
}

function Lines({
  group,
  incoming,
  from,
  to,
}: {
  group: string
  incoming: boolean
  from: string
  to: string
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['money-flow-lines', group, incoming, from, to],
    queryFn: () =>
      api.get<FlowLine[]>(
        `/reports/money-flows/lines?group=${encodeURIComponent(group)}` +
          `&incoming=${incoming}&from_date=${from}&to_date=${to}`,
      ),
  })
  if (isLoading) return <Loading what="Loading the entries" />
  if (error) return <ErrorNote error={error} />
  const rows = data ?? []
  if (rows.length === 0) return <Empty>Nothing behind this heading.</Empty>
  return (
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Entry</th>
          <th>Account</th>
          <th>Note</th>
          <th className="num">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((line, index) => (
          <tr key={`${line.entry_id}-${index}`}>
            <td className="muted date">{line.journal_date}</td>
            <td className="mono">{line.journal_number}</td>
            <td>{line.account_name}</td>
            <td className="muted">{line.memo ?? line.source_type ?? ''}</td>
            <td className="num">{formatInr(line.amount_paise)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Side({
  title,
  groups,
  incoming,
  from,
  to,
}: {
  title: string
  groups: Group[]
  incoming: boolean
  from: string
  to: string
}) {
  const [open, setOpen] = useState<string | null>(null)
  if (groups.length === 0) return <Empty>No money {incoming ? 'in' : 'out'} in this period.</Empty>
  const total = groups.reduce((sum, g) => sum + g.amount_paise, 0)
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>{title}</th>
            <th className="num">Entries</th>
            <th className="num">Amount</th>
            <th className="num">Share</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.group}>
              <td>
                {g.group}
                {!g.is_trading && (
                  <div className="faint" style={{ fontSize: 12 }}>
                    a movement, not {incoming ? 'income' : 'spend'}
                  </div>
                )}
              </td>
              <td className="num muted">{g.count}</td>
              <td className="num">{formatInr(g.amount_paise)}</td>
              <td className="num muted">
                {total ? `${Math.round((g.amount_paise / total) * 100)}%` : '—'}
              </td>
              <td className="num">
                <button
                  className="small"
                  onClick={() => setOpen(open === g.group ? null : g.group)}
                >
                  {open === g.group ? 'Hide' : 'Show'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && (
        <div style={{ marginTop: 12 }}>
          <Lines group={open} incoming={incoming} from={from} to={to} />
        </div>
      )}
    </>
  )
}

export default function MoneyFlows() {
  const [from, setFrom] = useState(financialYearStart())
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))

  const { data, isLoading, error } = useQuery({
    queryKey: ['money-flows', from, to],
    queryFn: () =>
      api.get<Flows>(`/reports/money-flows?from_date=${from}&to_date=${to}&months=true`),
  })

  return (
    <>
      <Card
        title="Period"
        hint="everything below is derived from posted entries that touched a bank account"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        }
        tight
      >
        <ErrorNote error={error} />
        {isLoading && <Loading what="Working out the flows" />}
        {data && (
          <div className="grid">
            <div className="card stat">
              <div className="label">Money in</div>
              <div className="value">{formatInr(data.in_total_paise)}</div>
              <div className="note">
                of which {formatInr(data.trading_in_paise)} was earned; the rest is money
                moved, not income
              </div>
            </div>
            <div className="card stat">
              <div className="label">Money out</div>
              <div className="value">{formatInr(data.out_total_paise)}</div>
              <div className="note">
                of which {formatInr(data.trading_out_paise)} was spent on running the
                business
              </div>
            </div>
            <div className="card stat">
              <div className="label">Net movement</div>
              <div className="value">{formatInr(data.net_paise)}</div>
              <div className="note">what the bank balances moved by, in total</div>
            </div>
          </div>
        )}
      </Card>

      {data && (
        <>
          <Card title="Money in" hint="grouped by what the other side of the entry was" tight>
            <Side title="Came from" groups={data.money_in} incoming from={from} to={to} />
          </Card>
          <Card title="Money out" hint="click a heading for the entries behind it" tight>
            <Side
              title="Went to"
              groups={data.money_out}
              incoming={false}
              from={from}
              to={to}
            />
          </Card>
          {data.by_month && data.by_month.length > 1 && (
            <Card title="Month by month" tight>
              <table>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="num">In</th>
                    <th className="num">Out</th>
                    <th className="num">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_month.map((m) => (
                    <tr key={m.month}>
                      <td className="mono">{m.month}</td>
                      <td className="num">{formatInr(m.in_paise)}</td>
                      <td className="num">{formatInr(m.out_paise)}</td>
                      <td className="num">{formatInr(m.net_paise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
          <Note kind="info">
            A loan received and a month's fees both add to the bank, and only one of them is
            revenue. The headings keep them apart, and the ones marked as movements are
            excluded from the earned and spent figures above.
          </Note>
        </>
      )}
    </>
  )
}
